import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { ChannelType } from 'src/app/models/enums/channel-type';
import { ManagementType } from 'src/app/models/enums/management-type';
import { ViewType } from 'src/app/models/enums/view-type';
import { IChannel } from 'src/app/models/interfaces/channel.interface';
import { MemoryService } from 'src/app/services/memory.service';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { ChannelFormContext } from '../channel-form-context';

@Component({
    selector: 'app-channel-form',
    templateUrl: './channel-form.component.html',
    styleUrls: ['./channel-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        FaIconComponent,
        MatTooltipModule,
        MatCheckboxModule,
        MatButtonModule,
        NgClass,
        RichToolTipDirective,
    ]
})
export class ChannelFormComponent extends ManagementFormComponent<IChannel> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;
    public ChannelType = ChannelType;

    /**
     * Infos du canal de réception à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('channel')
    public dataInput: Required<IChannel> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ChannelFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ChannelFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ChannelFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    public channelTypeLabels: Map<ChannelType, string> = new Map<ChannelType, string>();

    public managementTypeLabels: Map<ManagementType, string> = new Map<ManagementType, string>();

    public enumViewType = ViewType;

    public currentViewType: ViewType;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('channelUpdated')
    public dataOutput: EventEmitter<IChannel> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    constructor(private memoryService: MemoryService) {
        super();
        this.currentViewType = this.memoryService.getSelectedView().viewType;
    }
    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            this.channelTypeLabels.set(ChannelType.Fax, 'Fax');
            this.channelTypeLabels.set(ChannelType.Email, 'Email');
            this.channelTypeLabels.set(ChannelType.Sms, 'Sms');
            this.channelTypeLabels.set(ChannelType.Vocal, 'Vocal');
            this.channelTypeLabels.set(ChannelType.Chat, 'Chat');

            this.managementTypeLabels.set(ManagementType.Box, 'Gestion par bannettes');
            this.managementTypeLabels.set(ManagementType.Workflow, 'Gestion par workflow');
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IChannel> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            channelType: new UntypedFormControl(null, Validators.required),
            addressId: new UntypedFormControl(null, Validators.required),
            ocrEnabled: new UntypedFormControl(null),
            customerIdentificationEnabled: new UntypedFormControl(null),
            documentIdentificationEnabled: new UntypedFormControl(null),
            attributesAssignmentEnabled: new UntypedFormControl(null),
            ladEnabled: new UntypedFormControl(null),
            autoLinkEnabled: new UntypedFormControl(null),
            defaultBoxId: new UntypedFormControl(null),
            defaultStateId: new UntypedFormControl(null),
            viewId: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        // Force l'id de la vue car il n'est pas dans le formulaire
        this.dataValue.viewId = this.memoryService.getSelectedView().id;
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
